import { useEffect } from 'react'

import { useRequest } from 'ahooks'

import { scanCallbackApi } from '@eastcoal/api/auth/wechat/scanCallback'
import { showQrcodeApi } from '@eastcoal/api/auth/wechat/showQrcode'

import { KeySet } from '@eastcoal/common/types/utilities'

import { useScanQrcodeStore, useScanQrcodeStoreApi } from '../hooks/useStore'
import { ScanQrcodeAction, ScanQrcodeState } from '../store/interface'

import { ScanQrcodeStoreUpdatorProps } from './interface'

const useStoreUpdater = <K extends KeySet<ScanQrcodeState>>(
  key: K,
  value: ScanQrcodeState[K]
) => {
  const { setState } = useScanQrcodeStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <K extends KeySet<ScanQrcodeAction>>(
  setter: K,
  value: Parameters<ScanQrcodeAction[K]>[0]
) => {
  const { getState } = useScanQrcodeStoreApi()

  useEffect(() => {
    getState()[setter](value)
  }, [setter, value, getState])
}

export const ScanQrcodeStoreUpdator = ({
  autoLogin,
  onScan,
  onReload
}: ScanQrcodeStoreUpdatorProps) => {
  const {
    data: showResult,
    error: showError,
    refreshAsync
  } = useRequest(showQrcodeApi, {
    manual: false
  })

  const sceneId = useScanQrcodeStore(state => state.sceneId)

  const { data: scanResult } = useRequest(
    () =>
      scanCallbackApi({
        autoLogin,
        sceneId
      }),
    {
      manual: false,
      pollingInterval: 2000,
      pollingErrorRetryCount: 3,
      ready: !!sceneId
    }
  )

  useStoreUpdater('onScan', onScan)
  useStoreUpdater('onReload', onReload)
  useStoreUpdater('reloadApi', refreshAsync)

  useStoreSetter('updateShowError', showError)
  useStoreSetter('updateShowResult', showResult)
  useStoreSetter('updateScanResult', scanResult)

  return null
}
